import { RiArrowDownSLine, RiArrowUpSLine } from '@remixicon/react';
import { FC, useState, useMemo } from 'react';
import styles from './FilterDropdown.module.css';
import MenuDropDown from '../MenuDropDown';
import Tooltip, { ToolTipSize, TooltipVariant } from '../../atoms/Tooltip';
import {
  GroupByConfig,
  GroupTooltipConfig,
  Option,
} from '../../../types/SelectInput';
import { translate } from '../../../localization';
import hasValidValues from '../../../utils/hasValidValues';

type FilterDropdownProps = {
  name: string;
  list: Option[];
  isDisabled?: boolean;
  inputPlaceholder?: string;
  searchInputPlaceholder?: string;
  required?: boolean;
  multiSelect?: boolean;
  value: string[];
  handleSelection: (value: string | string[]) => void;
  className?: string;
  inputClassName?: string;
  isGroupDropDown?: boolean;
  groupingConfig?: GroupByConfig[];
  groupTooltips?: GroupTooltipConfig;
  unCategorizedListTitle?: string;
  enableSelectAll?: boolean;
  isSearchable?: boolean;
};

const FilterDropdown: FC<FilterDropdownProps> = ({
  name,
  list,
  inputPlaceholder = '',
  searchInputPlaceholder = '',
  required = false,
  multiSelect = false,
  handleSelection,
  isDisabled,
  value,
  className,
  inputClassName,
  groupingConfig,
  groupTooltips,
  unCategorizedListTitle = translate('others'),
  isGroupDropDown = false,
  enableSelectAll = false,
  isSearchable = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isTooltipVisible, setTooltipVisible] = useState(false);

  const handleClick = () => {
    if (!isDisabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleOptionSelection = (selectedValue: string | string[]) => {
    if (multiSelect) {
      handleSelection(selectedValue as string[]);
    } else {
      handleSelection(selectedValue as string);
      setIsOpen(false);
    }
  };

  const getSelectedOptionLabel = () => {
    const getLabel = (option: string) =>
      list?.find(entry => entry.name === option)?.label || inputPlaceholder;

    if (multiSelect) {
      if (value?.length === 1) {
        return getLabel(value[0]);
      }

      if (value?.length === list?.length) {
        return translate('all_selected');
      }

      return `${(value as string[]).length} selected`;
    }

    return getLabel(value[0]);
  };

  // Automatically disable single options in multiselect mode to match CapsuleDropdown behavior
  const processedList = useMemo(() => {
    if (multiSelect && list?.length === 1) {
      return list.map(option => ({
        ...option,
        isDisabled: true,
      }));
    }
    return list;
  }, [list, multiSelect]);

  return (
    <div className={className}>
      <div
        className={`${styles.input_wrapper} ${
          isDisabled ? styles['input_wrapper--disabled'] : ''
        } ${isOpen ? styles['input_wrapper--open'] : ''} ${inputClassName || ''}`}
        onClick={handleClick}
        id={name}
        title={inputPlaceholder}
        tabIndex={0}
        role='combobox'
        aria-expanded='false'
        aria-controls={`menu-dd-${name}`}
        aria-disabled={isDisabled}
        onKeyUp={() => {}}
      >
        <div className={styles['input_wrapper--inner']}>
          {/* Placeholder Label */}
          <p
            className={`${styles.placeholder_label} ${
              !(value?.length > 0 && hasValidValues(value))
                ? styles['placeholder_label--centered']
                : ''
            } ${isDisabled ? styles['placeholder_label--disabled'] : ''}`}
          >
            {inputPlaceholder}
            {required && <span className={styles.required}>*</span>}
          </p>

          {/* Selected Value */}
          {value?.length > 0 && hasValidValues(value) && (
            <div
              className={styles.tooltip_wrapper}
              onMouseEnter={() => setTooltipVisible(true)}
              onMouseLeave={() => setTooltipVisible(false)}
              role='button'
              tabIndex={0}
              aria-label='Selected value with tooltip'
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  setTooltipVisible(!isTooltipVisible);
                }
              }}
            >
              <Tooltip
                text={getSelectedOptionLabel()}
                showTooltip={isTooltipVisible}
                onClickedOutside={() => setTooltipVisible(false)}
                variant={TooltipVariant.TOP_RIGHT}
                size={ToolTipSize.REGULAR}
                theme='default'
                id={`filter-dropdown-tooltip-${name}`}
                classes={styles.filterDropdownTooltip}
              >
                <p
                  className={`${styles.selected_value} ${
                    isDisabled ? styles['input_wrapper--inner--disabled'] : ''
                  }`}
                >
                  {getSelectedOptionLabel()}
                </p>
              </Tooltip>
            </div>
          )}
        </div>

        {/* Arrow */}
        <div>
          {isOpen ? (
            <RiArrowUpSLine
              className={`${styles.down_arrow} ${
                isDisabled ? styles['down_arrow--disabled'] : ''
              }`}
              size={20}
            />
          ) : (
            <RiArrowDownSLine
              className={`${styles.down_arrow} ${
                isDisabled ? styles['down_arrow--disabled'] : ''
              }`}
              size={20}
            />
          )}
        </div>
      </div>
      <div className={styles.options_wrapper} id={`menu-dd-${name}`}>
        <div className={`${styles['options_wrapper--inner']}`}>
          <MenuDropDown
            selectedValue={multiSelect ? value : value?.[0] || ''}
            list={processedList}
            valueProperty='name'
            sortBy='label'
            isSorted
            isOpen={isOpen}
            multiSelect={multiSelect}
            onChange={handleOptionSelection}
            isShowSelectElement={false}
            onSelectTabClick={() => {
              if (!isDisabled) {
                setIsOpen(false);
              }
            }}
            wrapperClasses={isOpen ? styles['menu_dropdown--wrapper'] : ''}
            className={styles['menu_dropdown_list--wrapper']}
            searchInputPlaceholder={searchInputPlaceholder}
            groupingConfig={groupingConfig}
            groupTooltips={groupTooltips}
            unCategorizedListTitle={unCategorizedListTitle}
            categorizedDropdown={isGroupDropDown}
            enableSelectAll={enableSelectAll}
            isSearchable={isSearchable}
          />
        </div>
      </div>
    </div>
  );
};

export default FilterDropdown;
